import { motion, AnimatePresence } from 'motion/react';
import { Check, CircleAlert, CircleCheck, Loader2, SkipForward, Workflow, Wrench, X } from 'lucide-react';
import { useCrm, answerApproval } from '@/lib/crm';
import { useUi } from '@/lib/store';
import { ago } from '@/lib/format';
import { Button, cn, Empty, IconButton } from './ui';

/** Approval prompt for CRM write actions proposed by the agent. */
export function CrmApprovalDialog() {
  const req = useCrm((s) => s.approvals[0]);
  return (
    <AnimatePresence>
      {req && (
        <motion.div className="fixed inset-0 z-[60] flex items-center justify-center p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <motion.div
            initial={{ scale: 0.95, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10 }}
            className="ai-ring relative w-[520px] rounded-2xl bg-raised p-5 shadow-2xl"
          >
            <div className="mb-3 flex items-center gap-2.5">
              <span className="flex size-8 items-center justify-center rounded-xl ai-gradient text-white"><Workflow className="size-4" /></span>
              <div>
                <div className="text-[15px] font-semibold">Update your CRM?</div>
                <div className="text-[12px] text-muted">The AI wants to run <b className="text-fg">{req.tool}</b> on <b className="text-fg">{req.serverName}</b></div>
              </div>
            </div>
            {req.description && <p className="mb-3 text-[12.5px] text-muted">{req.description}</p>}
            <pre className="selectable mb-4 max-h-64 overflow-auto rounded-xl bg-bg/60 p-3 font-mono text-[12px] text-fg/90">{JSON.stringify(req.args, null, 2)}</pre>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => answerApproval(req.requestId, false)} icon={<X className="size-4" />}>Skip</Button>
              <Button variant="primary" onClick={() => answerApproval(req.requestId, true)} icon={<Check className="size-4" />} autoFocus>Approve</Button>
            </div>
            <p className="mt-3 text-[11.5px] text-faint">Switch to automatic logging in Settings → CRM to skip this prompt for non-destructive actions.</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Slide-over listing recent CRM agent runs and each tool call they made. */
export function CrmPanel() {
  const open = useCrm((s) => s.panelOpen);
  const setOpen = useCrm((s) => s.setPanel);
  const runs = useCrm((s) => s.runs);
  const setView = useUi((s) => s.setView);
  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          initial={{ x: 460, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 460, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 38 }}
          className="glass fixed bottom-3 right-3 top-[56px] z-40 flex w-[440px] flex-col overflow-hidden rounded-3xl bg-raised/90 shadow-2xl"
        >
          <div className="flex items-center gap-2.5 border-b border-line px-5 py-3.5">
            <Workflow className="size-5 text-accent" />
            <div className="flex-1 text-[14px] font-semibold">CRM activity</div>
            <Button size="sm" variant="ghost" onClick={() => (setOpen(false), setView({ kind: 'settings', tab: 'crm' }))}>Connections</Button>
            <IconButton label="Close" size="sm" onClick={() => setOpen(false)}><X /></IconButton>
          </div>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
            {runs.length === 0 ? (
              <Empty icon={<Workflow />} title="No CRM activity yet" hint="Turn on CRM logging in Settings, then sent emails are logged on the right record automatically." />
            ) : (
              runs.map((r) => (
                <div key={r.id} className="rounded-2xl border border-line bg-bg/40 p-3.5">
                  <div className="flex items-center gap-2">
                    {r.status === 'running' ? <Loader2 className="size-4 animate-spin text-accent" /> : r.status === 'done' ? <CircleCheck className={cn('size-4', r.wrote ? 'text-ok' : 'text-muted')} /> : <CircleAlert className="size-4 text-danger" />}
                    <span className="flex-1 truncate text-[13px] font-medium">{r.title}</span>
                    <span className="text-[11px] text-faint">{ago(new Date(r.startedAt).toISOString())}</span>
                  </div>
                  {r.summary && <div className="selectable mt-2 text-[12.5px] text-fg/85">{r.summary}</div>}
                  {r.steps.filter((s) => s.type === 'tool_call' || s.type === 'skipped').length > 0 && (
                    <div className="mt-2.5 space-y-1 border-t border-line pt-2.5">
                      {r.steps.filter((s) => s.type === 'tool_call' || s.type === 'skipped').map((s, i) => (
                        <div key={i} className="flex items-center gap-2 text-[11.5px] text-muted">
                          {s.type === 'skipped' ? <SkipForward className="size-3" /> : <Wrench className="size-3" />}
                          <span className="font-mono">{s.serverName} · {s.tool}</span>
                          {s.type === 'skipped' && <span className="text-warn">skipped</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
